import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import styles from "./TextInput.module.css";

/**
 * Form text input — Figma node 272:10133. A labelled field on a raised
 * surface, with optional hint text below and an error state. Used in the
 * footer modals (feedback + suggest-a-spot); not for the search modal,
 * which uses a different inline input.
 *
 * Pass `multiline` to render a <textarea> instead of <input>. Standard
 * native props (`value`, `onChange`, `placeholder`, `required`,
 * `autoComplete`, `name`, `type`, `inputMode`, …) flow through.
 */

type CommonProps = {
  /** Visible label above the field. Omit for unlabelled use. */
  label?: string;
  /** Caption beneath the field — switches to the error palette when
   *  `error` is true. */
  hint?: string;
  /** Error state: red border + error-coloured hint. */
  error?: boolean;
};

type SingleLineProps = CommonProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & {
    multiline?: false;
  };

type MultiLineProps = CommonProps &
  TextareaHTMLAttributes<HTMLTextAreaElement> & {
    multiline: true;
  };

export type TextInputProps = SingleLineProps | MultiLineProps;

export const TextInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextInputProps
>(function TextInput(props, ref) {
  const autoId = useId();
  // Pull every non-DOM prop off explicitly so the rest spread is safe
  // to apply to the native element. Letting `multiline` (or `label` /
  // `hint` / `error`) slip through triggers React's "unknown attribute"
  // warning and pollutes the DOM.
  const {
    label,
    hint,
    error,
    multiline,
    className,
    id: idProp,
    ...rest
  } = props as CommonProps & {
    multiline?: boolean;
    className?: string;
    id?: string;
  } & Record<string, unknown>;
  const id = idProp ?? autoId;
  const hintId = hint ? `${id}-hint` : undefined;

  const fieldClass = [
    styles.field,
    error ? styles.fieldError : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.wrap}>
      {label && (
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
      )}
      {multiline ? (
        <textarea
          ref={ref as React.Ref<HTMLTextAreaElement>}
          id={id}
          className={fieldClass}
          aria-invalid={error || undefined}
          aria-describedby={hintId}
          {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <input
          ref={ref as React.Ref<HTMLInputElement>}
          id={id}
          className={fieldClass}
          aria-invalid={error || undefined}
          aria-describedby={hintId}
          {...(rest as InputHTMLAttributes<HTMLInputElement>)}
        />
      )}
      {hint && (
        <p
          id={hintId}
          className={[styles.hint, error ? styles.hintError : null]
            .filter(Boolean)
            .join(" ")}
        >
          {hint}
        </p>
      )}
    </div>
  );
});
